<template>
  <div class="ui-page">
    <app-filter-toolbar>
      <template #create><app-button v-if="canSubmit" design="new" @click.native="$refs.requestModal.open()">New leave request</app-button></template>
      <app-select id="sales-leave-employee" v-model="employee" design="new" :options="employeeOptions" :clearable="true" label="full_name" placeholder="Employee" aria-label="Employee" @search="onEmployeeSearch" @input="onFiltersChange" />
      <template #selection>
        <app-filters v-if="section === 'requests'" v-model="status" design="new" refined weight="medium" :filters="statusOptions" name="sales-leave-status" aria-label="Request status" @change="onStatusChange" />
        <app-filters v-else v-model="leaveStatus" design="new" refined weight="medium" :filters="leaveStatusOptions" name="sales-leave-period-status" aria-label="Leave status" @change="onLeaveStatusChange" />
      </template>
      <template #secondary-selection>
        <app-filters v-if="section === 'requests'" v-model="kind" design="new" refined weight="medium" :filters="kindOptions" name="sales-leave-request-kind" aria-label="Request kind" @change="onFiltersChange" />
        <app-filters v-else v-model="changePending" design="new" refined weight="medium" :filters="changePendingOptions" name="sales-leave-change-pending" aria-label="Date change" @change="onFiltersChange" />
      </template>
      <template v-if="hasFilters" #clear><app-clear-filters-button design="new" icon-only @clear="clearFilters" /></template>
    </app-filter-toolbar>
    <app-content-skeleton v-if="loading" label="Loading leaves" :columns="tableColumns" min-width="1200px" />
    <app-error-state v-else-if="loadError" icon-only :message="loadError" @retry="fetch" />
    <template v-else>
      <div v-if="groups.length" class="ui-stack">
        <app-card v-for="group in groups" :key="group.code" design="new" variant="table" header-density="compact" :title="group.title" :count="group.rows.length">
          <sales-leave-requests-table v-if="section === 'requests'" :items="group.rows" :loading="loading" :filter-status="status" grouped show-employee workspace @approve="approve" @reject="$refs.rejectModal.open($event)" @cancel="cancel" @preview="$refs.calendarPreview.open($event)" />
          <sales-leave-leaves-table v-else :items="group.rows" :loading="loading" grouped show-employee workspace @change="$refs.changeModal.open($event)" @cancel-leave="$refs.cancelModal.open($event, 'cancel')" @open-request="openRequest" />
        </app-card>
      </div>
      <app-no-results v-else design="new" :title="section === 'requests' ? 'No leave requests' : 'No leaves'" text="" />
      <app-pagination v-if="groups.length" design="new" placement="embedded" :page="page" :pages="pages" :count="count" :visible-count="items.length" :page-size="pageSize" :loading="loading" @change="onPageChange" />
    </template>

    <sales-leave-request-modal ref="requestModal" mode="managed" name="sales-leave-workspace-request" @created="fetch" />
    <sales-leave-change-modal
      ref="changeModal"
      name="sales-leave-workspace-change"
      :submit-request="submitChange"
      :preview-request="previewChange"
      @created="fetch"
    />
    <leave-mutation-modal ref="cancelModal" name="sales-workspace-cancellation" :submit-request="submitCancellation" @saved="fetch" />
    <sales-leave-reject-modal
      ref="rejectModal"
      name="sales-leave-workspace-reject"
      :reject-request="reject"
      @done="fetch"
    />
    <leave-request-schedule-preview
      ref="calendarPreview"
      :sales="true"
      @approve="approve"
      @reject="$refs.rejectModal.open($event)"
    />
  </div>
</template>

<script>
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import debounce from 'lodash/debounce'
import {
  getEmployeesListSearch,
  getEmployeesSalesLeaveRequests,
  getEmployeesSalesLeaves,
  postEmployeesSalesLeaveChangeRequest,
  postEmployeesSalesLeaveCancellation,
  postEmployeesSalesLeaveRequestApprove,
  postEmployeesSalesLeaveRequestCancel,
  postEmployeesSalesLeaveRequestPreview,
  postEmployeesSalesLeaveRequestReject,
} from '@/http'
import SalesLeaveChangeModal from './SalesLeaveChangeModal'
import LeaveMutationModal from '@/views/employees/leaves/LeaveMutationModal.vue'
import SalesLeaveLeavesTable from './SalesLeaveLeavesTable'
import SalesLeaveRejectModal from './SalesLeaveRejectModal'
import SalesLeaveRequestModal from './SalesLeaveRequestModal'
import SalesLeaveRequestsTable from './SalesLeaveRequestsTable'
import LeaveRequestSchedulePreview from '@/views/employees/leaves-old/LeaveRequestSchedulePreview'
import { SALES_LEAVE_VIEW_ROLES } from './salesLeave'

const PAGE_SIZE = 20

export default {
  name: 'SalesLeaveWorkspace',
  components: {
    AppContentSkeleton,
    AppErrorState,
    SalesLeaveChangeModal,
    LeaveMutationModal,
    SalesLeaveLeavesTable,
    SalesLeaveRejectModal,
    SalesLeaveRequestModal,
    SalesLeaveRequestsTable,
    LeaveRequestSchedulePreview,
  },
  data() {
    return {
      status: 'requested',
      statusOptions: [
        { label: 'Requested', value: 'requested' },
        { label: 'Approved', value: 'approved' },
        { label: 'Rejected', value: 'rejected' },
        { label: 'Cancelled', value: 'cancelled' },
        { label: 'All', value: '' },
      ],
      kind: '',
      kindOptions: [
        { value: '', label: 'All' },
        { value: 'create', label: 'New leave' },
        { value: 'change_dates', label: 'Date change' },
        { value: 'cancel_leave', label: 'Cancellation' },
      ],
      leaveStatus: 'upcoming',
      leaveStatusOptions: [
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'in_process', label: 'In progress' },
        { value: 'past', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: '', label: 'All' },
      ],
      employee: null,
      employeeOptions: [],
      changePending: '',
      changePendingOptions: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Change pending' },
      ],
      items: [],
      count: 0,
      pages: 1,
      page: 1,
      pageSize: PAGE_SIZE,
      loading: false,
      loadError: '',
      fetchRequestId: 0,
    }
  },
  computed: {
    section() {
      return this.$route.name === 'employees-sales-leaves-list' ? 'leaves' : 'requests'
    },
    tableColumns() {
      const table = this.section === 'requests' ? SalesLeaveRequestsTable : SalesLeaveLeavesTable
      return table.computed.columns.call({ showEmployee: true, grouped: true, filterStatus: this.status })
    },
    groups() {
      const groups = new Map()
      this.items.forEach(row => {
        const type = row.type || {}
        const code = type.code || type.name || 'other'
        if (!groups.has(code)) groups.set(code, { code, title: type.name || 'Other leave', rows: [] })
        groups.get(code).rows.push(row)
      })
      return Array.from(groups.values())
    },
    hasFilters() {
      return Boolean(this.employee || (this.section === 'requests' ? this.status !== 'requested' || this.kind : this.leaveStatus !== 'upcoming' || this.changePending))
    },
    canSubmit() {
      // Marketing supervisors read and submit; approval stays with Sales supervisors.
      return this.$store.state.userGroups.some(group => SALES_LEAVE_VIEW_ROLES.includes(group.code))
    },
  },
  created() {
    if (this.$route.query.kind === 'change_dates') this.kind = 'change_dates'
    this.fetch()
  },
  methods: {
    submitCancellation(leave, kind, payload) { return postEmployeesSalesLeaveCancellation(leave.id, payload) },
    params() {
      const params = { page: this.page, page_size: this.pageSize }
      if (this.employee) params.employee = this.employee.id
      if (this.section === 'requests') {
        if (this.status) params.status = this.status
        if (this.kind) params.kind = this.kind
      } else {
        if (this.changePending) params.change_pending = true
        if (this.leaveStatus) params.period_status = this.leaveStatus
      }
      return params
    },
    fetch() {
      const requestId = ++this.fetchRequestId
      this.loading = true
      this.loadError = ''
      const request = this.section === 'requests' ? getEmployeesSalesLeaveRequests : getEmployeesSalesLeaves
      return request(this.params())
        .then(res => {
          if (requestId !== this.fetchRequestId) return
          this.items = res.data.results
          this.count = res.data.count
          this.pages = res.data.pages
        })
        .catch(() => { if (requestId === this.fetchRequestId) this.loadError = 'Unable to load leaves' })
        .finally(() => { if (requestId === this.fetchRequestId) this.loading = false })
    },
    clearFilters() {
      this.employee = null
      this.status = 'requested'
      this.kind = ''
      this.leaveStatus = 'upcoming'
      this.changePending = ''
      this.page = 1
      this.fetch()
    },
    onStatusChange(status) {
      this.status = status
      this.page = 1
      this.fetch()
    },
    onLeaveStatusChange(status) {
      this.leaveStatus = status
      this.page = 1
      this.fetch()
    },
    onFiltersChange() {
      this.page = 1
      this.fetch()
    },
    onPageChange(page) {
      this.page = page
      this.fetch()
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployees(loading, search)
    },
    searchEmployees: debounce(function (loading, search) {
      if (!search) {
        this.employeeOptions = []
        loading(false)
        return
      }
      getEmployeesListSearch({ search, resident: false })
        .then(res => { this.employeeOptions = res.data })
        .finally(() => loading(false))
    }, 350),
    approve(row) {
      postEmployeesSalesLeaveRequestApprove(row.id)
        .then(() => {
          this.$notify({ type: 'success', title: 'Request approved' })
          this.fetch()
        })
        .catch(() => this.fetch())
    },
    reject(row, reason) {
      return postEmployeesSalesLeaveRequestReject(row.id, { decision_reason: reason })
    },
    cancel(row) {
      postEmployeesSalesLeaveRequestCancel(row.id)
        .then(() => {
          this.$notify({ type: 'success', title: 'Request cancelled' })
          this.fetch()
        })
        .catch(() => {})
    },
    submitChange(leave, payload) {
      return postEmployeesSalesLeaveChangeRequest(leave.id, payload)
    },
    previewChange(leave, payload) {
      return postEmployeesSalesLeaveRequestPreview({
        employee: leave.employee.id,
        leave: leave.id,
        ...payload,
      })
    },
    openRequest() {
      this.$router.push({ name: 'employees-sales-leaves' })
    },
  },
}
</script>
